<script lang="ts">
    import Modal from "./Modal.svelte";
    import AddTransactionForm from "./AddTransactionForm.svelte";
    import TransactionsByBill from "./TransactionsByBill.svelte";
    import ChangeBillForm from "./ChangeBillForm.svelte";
    import AddBillForm from "./AddBillForm.svelte";
    import { getTodaysDate, getTotalBills, fetchJson } from "./utils";
    import { API_URL, TRANSBYBILL_LIMIT } from "./config";
    import type { Bill, Transaction, TransactionFormData } from "./types";

    type ApiBill = Omit<Bill, 'inactive'> & { inactive: number };

    const handleFetch = <T = unknown>(path: string, options?: RequestInit): Promise<T> =>
        fetchJson<T>(API_URL + path, options);

    let showModal: boolean = false;
    let showInactive: boolean = false;
    let responseSuccess: boolean = false;
    let showTransactions: boolean = false;
    let showEditBill: boolean = false;
    let showAddBill: boolean = false;
    let bills: Bill[] = [];
    let billToPay: Bill = { id: 0, name: '', amount: 0, duedate: '', frequency: '', url: '', inactive: false, fk_users: 0 };
    let transByBill: Transaction[] = [];
    let lastTransBillId: number | string = "";
    let loading: boolean = false;
    let loadError: string = "";
    let totalBills: number;

    const toggleModal = (): void => { showModal = !showModal; };
    const toggleInactive = (): void => {
        showInactive = !showInactive;
        totalBills = getTotalBills(bills, showInactive);
    };
    const toggleTransactions = (): void => { showTransactions = !showTransactions; };
    const toggleEditBill = (): void => { showEditBill = !showEditBill; };
    const toggleAddBill = (): void => { showAddBill = !showAddBill; };

    const getBills = (): void => {
        loading = true;
        handleFetch<{ bill_list: ApiBill[] }>("bills")
            .then((data) => {
                bills = data.bill_list.map((b) => ({ ...b, inactive: b.inactive === 1 }));
                loading = false;
            })
            .catch((error: Error) => {
                loadError = error.message;
                loading = false;
            });
    };

    getBills();

    $: totalBills = getTotalBills(bills, showInactive);
    $: reload(responseSuccess);

    const handleSkip = (e: MouseEvent, id: number): void => {
        billToPay = bills.find((bill) => bill.id === id) ?? billToPay;
        if (
            confirm(
                `Are you sure you want to skip a payment for ${billToPay.name}?`,
            )
        ) {
            handleFetch<{ success: boolean }>("skipPayment", {
                method: "POST",
                body: JSON.stringify({ bill_id: id }),
            })
                .then((data) => (responseSuccess = data.success))
                .catch((error: Error) => console.log(error.message));
        }
    };

    const reload = (perform: boolean): void => {
        if (perform) {
            location.reload();
        }
    };

    const handlePay = (e: MouseEvent, id: number): void => {
        billToPay = bills.find((bill) => bill.id === id) ?? billToPay;
        toggleModal();
    };

    const handleTransByBill = (e: MouseEvent, id: number): void => {
        if (id !== lastTransBillId) {
            loading = true;
            loadError = "";
            handleFetch<{ transaction_list: Transaction[] }>(`transactionsByBillId/${id}/${TRANSBYBILL_LIMIT}`)
                .then((data) => {
                    transByBill = data.transaction_list;
                    lastTransBillId = id;
                    loading = false;
                })
                .catch((error: Error) => {
                    loadError = error.message;
                    loading = false;
                });
        }
        showTransactions = true;
    };

    const handleEditBill = (e: MouseEvent, id: number): void => {
        billToPay = bills.find((bill) => bill.id === id) ?? billToPay;
        toggleEditBill();
    };

    const addTransaction = (e: CustomEvent<TransactionFormData>): void => {
        const transaction = e.detail;
        handleFetch<{ success: boolean }>("addTransaction", {
            method: "POST",
            body: JSON.stringify({
                payee: transaction.payee,
                payamount: transaction.payAmount,
                paydate: transaction.payDate,
                memo: transaction.memo,
                bill_id: transaction.billId,
            }),
        })
            .then((data) => (responseSuccess = data.success))
            .catch((error: Error) => console.log(error.message));
        showModal = false;
    };

    const editBill = (e: CustomEvent<Bill>): void => {
        const bill = e.detail;
        handleFetch<unknown>("editBill", {
            method: "POST",
            body: JSON.stringify({
                billname: bill.name,
                amount: bill.amount,
                duedate: bill.duedate,
                frequency: bill.frequency,
                url: bill.url,
                inactive: bill.inactive ? 1 : 0,
                bill_id: bill.id,
            }),
        })
            .then((data) => console.log(data))
            .catch((error: Error) => console.log("Error: " + error.message));
        showEditBill = false;
    };

    const addBill = (e: CustomEvent<Omit<Bill, 'id' | 'fk_users'>>): void => {
        const bill = e.detail;
        handleFetch<{ success: boolean }>("addBill", {
            method: "POST",
            body: JSON.stringify({
                billname: bill.name,
                amount: bill.amount,
                duedate: bill.duedate,
                frequency: bill.frequency,
                url: bill.url,
                inactive: bill.inactive ? 1 : 0,
                fk_users: bills[0]?.fk_users,
            }),
        })
            .then((data) => (responseSuccess = data.success))
            .catch((error: Error) => console.log("Error: " + error.message));
        showAddBill = false;
    };
</script>

<Modal {showModal} on:click={toggleModal} on:close={toggleModal}>
    <AddTransactionForm
        on:addTransaction={addTransaction}
        payee={billToPay.name}
        payAmount={String(billToPay.amount)}
        payDate={getTodaysDate()}
        billId={billToPay.id}
    />
</Modal>

<Modal {showTransactions} on:click={toggleTransactions} on:close={toggleTransactions}>
    <TransactionsByBill transactions={transByBill} {loading} {loadError} />
</Modal>

<Modal {showEditBill} on:click={toggleEditBill} on:close={toggleEditBill}>
    <ChangeBillForm on:editBill={editBill} bill={billToPay} />
</Modal>

<Modal {showAddBill} on:click={toggleAddBill} on:close={toggleAddBill}>
    <AddBillForm on:addBill={addBill} />
</Modal>

<main>
    <div class="container">
        <div id="app-title" class="box page-title">PyBill</div>
        <div class="box toolbar">
            <button class="add-bill-button" on:click={toggleAddBill} aria-label="Add bill">+</button>
            <label for="chkShowInactive">
                Show inactive <input
                    type="checkbox"
                    id="chkShowInactive"
                    on:click={toggleInactive}
                />
            </label>
        </div>
    </div>
    <div class="container">
        {#each bills as bill (bill.id)}
            {#if !bill.inactive || showInactive}
                <div
                    class="item {bill.inactive
                        ? 'inactive'
                        : Math.ceil(
                                (new Date(
                                    bill.duedate + ' 00:00:00',
                                ).getTime() -
                                    new Date().getTime()) /
                                    (1000 * 3600 * 24),
                            ) < 10
                            ? 'due-soon'
                            : ''}"
                >
                    <div class="card-title">
                        <div class="card-title-item">
                            <button
                                class="card-icon-button"
                                on:click={(e) => handleTransByBill(e, bill.id)}
                                aria-label="Show last 10 transactions"
                            >
                                <img src="notepad.png" alt="Show last 10 transactions" class="card-icon" />
                            </button>
                        </div>
                        <div class="card-title-item">{bill.name}</div>
                        <div class="card-title-item">
                            <button
                                class="card-icon-button"
                                on:click={(e) => handleEditBill(e, bill.id)}
                                aria-label="Edit bill"
                            >
                                <img src="pencil.png" alt="" class="card-icon" />
                            </button>
                        </div>
                    </div>
                    <div>Due date: {bill.duedate}</div>
                    <div>Amount: ${bill.amount}</div>
                    <div class="show-url">
                        <a target="_blank" href={bill.url}>{bill.url}</a>
                    </div>
                    <div>
                        <button
                            class="button-primary"
                            on:click={(e) => handlePay(e, bill.id)}>pay</button
                        >
                        <button
                            class="button-primary"
                            on:click={(e) => handleSkip(e, bill.id)}
                            >skip</button
                        >
                    </div>
                </div>
            {/if}
        {:else}
            <div class="box">
                {#if loading}
                    <div class="loader"></div>
                {:else}
                    <div>{loadError}</div>
                    <div>There are no bills to show.</div>
                {/if}
            </div>
        {/each}
    </div>
    <div class="box">Total bills: {totalBills}</div>
    <div class="foot-font">
        Icons made by <a href="https://www.freepik.com" title="Freepik"
            >Freepik</a
        >
        from
        <a href="https://www.flaticon.com/" title="Flaticon">www.flaticon.com</a
        >
    </div>
</main>

<style>
    main {
        text-align: center;
        padding: 1em;
        /* max-width: 240px; */
        margin: 0 auto;
    }

    @media (min-width: 640px) {
        main {
            max-width: none;
        }
    }

    .page-title {
        font-weight: bold;
        font-size: 1.3em;
    }

    .loader {
        width: 100px;
        height: 100px;
        border-radius: 100px;
        border-top: 5px solid red;
        border-left: 3px solid brown;
        border-bottom: 1px solid purple;
        border-right: transparent;
        animation: spinner 0.8s linear infinite;
    }

    @keyframes spinner {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    .container {
        display: flex;
        flex-flow: row wrap;
        justify-content: center;
    }

    .box {
        margin: 0.5em;
    }

    .item {
        flex: 1 1 auto;
        margin: 0.5em;
        border-radius: 10px;
        padding: 1em;
        max-width: 260px;
        overflow: hidden;
        text-overflow: ellipsis;
        background-color: white;
        box-shadow:
            0 4px 8px 0 rgba(0, 0, 0, 0.2),
            0 5px 10px 0 rgba(0, 0, 0, 0.19);
    }

    .inactive {
        background-color: lightpink;
    }

    .due-soon {
        background-color: lightgoldenrodyellow;
    }

    #app-title {
        flex: 1;
        text-align: left;
    }

    .card-title {
        background-color: gray;
        color: whitesmoke;
        font-weight: bold;
        margin: -0.5em -0.5em 1em -0.5em;
        padding: 0.5em;
        border-radius: 5px;
        display: flex;
        min-width: 230px;
    }

    .card-title-item {
        flex: 1 1 auto;
        padding-left: 0.25em;
        padding-right: 0.25em;
    }

    .card-icon-button {
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        cursor: pointer;
        display: inline-block;
        height: 18px;
        width: 18px;
    }

    .card-icon {
        height: 18px;
        width: 18px;
    }

    .show-url {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 250px;
        margin: auto;
        padding-bottom: 1em;
    }

    .foot-font {
        font-size: 0.75rem;
    }

    .toolbar {
        display: flex;
        align-items: center;
        gap: 0.75em;
    }

    .add-bill-button {
        background-color: #4caf50;
        color: white;
        border: none;
        border-radius: 50%;
        width: 28px;
        height: 28px;
        font-size: 1.2em;
        line-height: 1;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
    }

    .add-bill-button:hover {
        background-color: #388e3c;
    }
</style>
